import React from 'react';
import PropTypes from 'prop-types';
import DragSelectComponent from '../Components/DragSelectComponent';
import {
  Button,
  Col,
  OverlayTrigger,
  Panel,
  Popover,
  Row,
  Table
} from 'react-bootstrap';
import { messaging, nodeIssues as nodeIssueHelpers, unicode } from '../helpers';
import ConditionalComponent from '../Components/ConditionalComponent';
import NodeHealthSelectorContainer from './../Containers/NodeHealthSelectorContainer';
import SearchComponent from '../Components/SearchComponent';
import { usePromiseTracker } from 'react-promise-tracker';

const getColumnString = (sortedNode, columnHeader) => {
  const value = sortedNode === columnHeader ?
    columnHeader + unicode.DOWN_POINTING_TRIANGLE :
    columnHeader;
  return value;
};

const generateColHeader = (nodeNames, sortedNode, onSort) => {
  const nodeLength = nodeNames.length;
  const result = [
    <tr key="nodes-col-header">
      <th>&nbsp;</th>
      <th>&nbsp;</th>
      <th
        className={'cell-std'}
        colSpan={nodeLength}
        scope="colgroup"
      >
        Nodes
      </th>
      <td>&nbsp;</td>
    </tr>,
    <tr key="nodes-row">
      <th>&nbsp;</th>
      <th>&nbsp;</th>
      <th
        key="sum-col-header"
        scope="col"
        title="click to sort by sum"
        onClick={() => onSort(undefined)}
      >
        Sum of Nodes
      </th>
      {nodeNames.map(n => n !== nodeIssueHelpers.UNKNOWN_NODE ?
        <th
          key={n}
          scope="col"
          title="click to sort by column"
          onClick={() => onSort(n)}
        >
          {getColumnString(sortedNode, n)}
        </th> :
        <th key={n} scope="col">{n}</th>)}
      <td>&nbsp;</td>
    </tr>
  ];
  return result;
};

/**
 * Generates a cell for display in the table
 * @param {int} issueCount -> the count to display within the cell
 * @param {string} nodeName -> name of the respective owner node, stored in cell dataset
 * @param {string} issueId -> id of the respective owner issue, stored in cell dataset
 * @param {string} groupDescriptor -> a nodeName OR issueId representing a cell
 *  that contains a summation, stored in cell dataset
 */
const generateCell = ({
  issueCount,
  nodeName,
  issueId,
  groupDescriptor,
  matchesSearch
}) => {
  const hasValue = issueCount > 0;
  const searchMatching = matchesSearch;
  // determine if not a group, else save the relevant name or id of the group
  const id = groupDescriptor ?
    `${groupDescriptor}-group` :
    `${nodeName}-${issueId}`;
  return (
    <td
      key={id}
      id={id}
      className={'node-draggable'}
      data-search-matching-cell={searchMatching || undefined}
      data-concerning-cell={hasValue || undefined}
      data-group={groupDescriptor}
      data-issue-id={issueId}
      data-node-name={nodeName}
    >
      {issueCount}
    </td>
  );
};
generateCell.propTypes = {
  issueCount: PropTypes.number.isRequired,
  nodeName: PropTypes.string,
  issueId: PropTypes.string,
  groupDescriptor: PropTypes.string,
  matchesSearch: PropTypes.bool
};

const buildTable = (
  activeIssuesByNodeNameById,
  activeIssueCountsLookup,
  issueTypesRowTitles,
  nodesColumnTitles,
  nodeIssueTypesById,
  sortedNode,
  onSort,
  searchTerm
) => {
  if (issueTypesRowTitles.length === 0 ||
    nodesColumnTitles.length === 0 ||
    Object.keys(activeIssueCountsLookup).length === 0) {
    return null;
  }

  const issueIds = issueTypesRowTitles;
  const rowHeader =
    <th
      key="issues-row-header"
      className={'cell-std'}
      rowSpan={issueIds.length + 1}
      scope="rowgroup"
    >
      Issues
    </th>;

  // build the rows
  const rows = issueIds.reduce((rowAcc, issueId) => {
    const issueType = nodeIssueTypesById[issueId] && nodeIssueTypesById[issueId].friendly;
    const issuesByNode = activeIssuesByNodeNameById[issueId];

    // build each row header
    const baseRowHeader =
      <th
        key={issueId}
        className="cell-std"
        rowSpan={1}
        scope="rowgroup"
      >
        {`${issueId}: ${issueType}` || issueId}
      </th>;

    // create one cell for every node
    const cells = nodesColumnTitles.reduce((cellAcc, nodeName) => {
      const issues = issuesByNode[nodeName];
      const matchesSearch = searchTerm !== '' &&
      issues && Array.isArray(issues) &&
      issues.some(issue => `${issue.job_name}_${issue.job_number}`.includes(searchTerm));
      const cellResult = generateCell({
        issueCount: issues.length,
        nodeName,
        issueId,
        matchesSearch
      });
      return [...cellAcc, cellResult];
    }, []);
    const cellsWithSum = [
      baseRowHeader,
      generateCell({
        issueCount: activeIssueCountsLookup[issueId],
        groupDescriptor: issueId
      }),
      ...cells
    ];

    // compile row with cells and the sum column
    const rowResult = [
      ...rowAcc,
      <tr key={'sum-row-' + issueId}>{[...cellsWithSum]}</tr>
    ];
    return rowResult;
  }, []);

  // define a summed row, place with spanning row header
  const rowHeaders = [
    rowHeader,
    <th key="sum-issues" rowSpan={1} scope="rowgroup">
      Sum of Issues
    </th>,
    generateCell({
      issueCount: activeIssueCountsLookup.total,
      groupDescriptor: 'total'
    }),
    ...nodesColumnTitles.map(n => generateCell({
      issueCount: activeIssueCountsLookup[n],
      groupDescriptor: n
    }))
  ];

  return (
    <Table
      style={{
        'whiteSpace': 'nowrap',
        textAlign: 'center',
        verticalAlign: 'middle'
      }}
      responsive
    >
      <thead>
        {generateColHeader(nodesColumnTitles, sortedNode, onSort)}
      </thead>
      <tbody>
        <tr>
          {[...rowHeaders]}
        </tr>
        {[...rows]}
      </tbody>
    </Table>
  );
};

/**
 * Displays a table summarizing active node issues, allowing users to select
 * from the cells which then populates another table with more details
 * based on the selection
 * @param {any} activeIssues - list of issues to summarize in table
 */
const NodeHealthSummaryComponent = (props) => {
  const {
    activeIssueCountsLookup,
    activeIssuesByNodeNameById,
    issueTypesRowTitles,
    nodesColumnTitles,
    nodeIssueTypesById,
    sortedNode,
    onSelectionComplete,
    onSort,
    onSearchChange,
    searchTerm
  } = props;
  const holdKey = 'ctrl';

  const { promiseInProgress } = usePromiseTracker();
  const displayMessage = promiseInProgress ?
    'Loading Data...' :
    'No Active Issues To Show';

  const table = buildTable(
    activeIssuesByNodeNameById,
    activeIssueCountsLookup,
    issueTypesRowTitles,
    nodesColumnTitles,
    nodeIssueTypesById,
    sortedNode,
    onSort,
    searchTerm);
  return (
    <div>
      <div style={{ marginLeft:'25px' }}>
        <Row>
          <NodeHealthSelectorContainer />
          <ConditionalComponent condition={
            activeIssueCountsLookup &&
              activeIssueCountsLookup['total'] > 0}
          >
            <SearchComponent
              onChange={onSearchChange}
              id="node-health-search-form"
              label="Search by Job Name, Number, or Name_Number"
            />
          </ConditionalComponent>
        </Row>
        <br />
      </div>
      <DragSelectComponent
        dragContainer={'.drag-select-area'}
        selectableTargets={['.node-draggable']}
        hitDetectionPercentile={1}
        continueSelectKey={holdKey}
        onSelectionComplete={(e) => {
          // selected property will hold array of table cells selected
          // we only care about the data they hold
          const dataArray = e.selected.map(td => td.dataset);

          onSelectionComplete(dataArray);
        }}
      />
      <Row className={'drag-select-area'}>
        <Col>
          <Panel bsStyle={'primary'} style={{ marginLeft: '2%', marginRight: '2%' }}>
            <Panel.Heading>
              <Panel.Title>
                {'Node Health Summary'}
                <div style={{ float: 'right' }}>
                  {`Multi select: '${holdKey}' key`}
                  <OverlayTrigger
                    trigger="click"
                    rootClose
                    placement="left"
                    overlay={(
                      <Popover
                        id="multi-select-popover"
                        title="How Do I Multi Select?"
                      >
                        {messaging.DRAG_MULTISELECT_POPOVER_BODY}
                      </Popover>)}
                  >
                    <Button
                      style={{
                        display: 'inline',
                        marginLeft: '5px',
                        marginTop: '-5px'
                      }}
                    >
                      ?
                    </Button>
                  </OverlayTrigger>
                </div>
              </Panel.Title>
            </Panel.Heading>
            <Panel.Body>
              {table || displayMessage}
            </Panel.Body>
          </Panel>
        </Col>
      </Row>
    </div>
  );
};

NodeHealthSummaryComponent.propTypes = {
  activeIssueCountsLookup: PropTypes.object.isRequired,
  activeIssuesByNodeNameById: PropTypes.object.isRequired,
  issueTypesRowTitles: PropTypes.array.isRequired,
  nodesColumnTitles: PropTypes.array.isRequired,
  nodeIssueTypesById: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      friendly: PropTypes.string.isRequired
    })
  ).isRequired,
  sortedNode: PropTypes.string,
  onSelectionComplete: PropTypes.func,
  onSort: PropTypes.func,
  onSearchChange: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired
};

export default NodeHealthSummaryComponent;


// WEBPACK FOOTER //
// src/Components/NodeHealthSummaryComponent.jsx